import { type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Dialog, DialogClose, DialogContent } from './dialog'
import { Button, type ButtonProps } from './button'
import { Card } from '@/design-system/components/layout/card'
import { cn } from '@/lib/utils'

/**
 * ConfirmDialog — «Подтверждение действия» (docs!84): запрос подтверждения ПЕРЕД действием,
 * которое определяет вызывающий интерфейс. Компонент НЕ знает смысла действия и НЕ выполняет его
 * сам — он лишь показывает заголовок, сообщение и переданные кнопки, каждая со своим `onClick`.
 *
 * Построен поверх примитива `Dialog` (Radix): фокус-ловушка, закрытие по Escape/клику вне панели
 * и `z-overlay` берутся «из коробки». Диалог КОНТРОЛИРУЕМЫЙ — открытием/закрытием и состоянием
 * кнопок во время выполнения управляет вызывающий (`open`, `disabled`, подписи кнопок).
 *
 * Значения по умолчанию применяются НЕЗАВИСИМО для каждого непереданного параметра:
 * — заголовок не передан/пустой → «Подтвердите действие»;
 * — сообщение не передано/пустое → «Вы уверены, что хотите продолжить?»;
 * — список кнопок не передан/пуст → «Подтвердить» (primary, событие подтверждения) + «Отмена»
 *   (secondary, событие отмены). ВАЖНО: непустой переданный список ПОЛНОСТЬЮ заменяет дефолтные
 *   кнопки — компонент НЕ подставляет дефолты для отдельных полей кнопки из списка.
 *
 * Крестик закрытия и Escape шлют вызывающему `onClose` (закрытие без выбора кнопки). Заголовок —
 * доступное имя диалога (aria), сообщение — доступное описание (aria-describedby).
 */
export interface ConfirmDialogButton {
  /** Подпись кнопки. */
  label: ReactNode
  /** Оформление кнопки (по умолчанию — как у `Button`: primary). */
  variant?: ButtonProps['variant']
  /** Недоступность (например, пока идёт выполнение действия). */
  disabled?: boolean
  /** Действие по нажатию — задаёт вызывающий; компонент только вызывает его. */
  onClick?: () => void
}

export interface ConfirmDialogProps {
  /** Открыт ли диалог (контролируемый). */
  open: boolean
  /** Заголовок; пустой/не передан → «Подтвердите действие». */
  title?: string
  /** Текст сообщения; пустой/не передан → «Вы уверены, что хотите продолжить?». */
  message?: string
  /**
   * Дополнительная разметка НАД сообщением — когда одного абзаца мало: список объектов, которых
   * касается действие (например, найденные рабочие места с сотрудниками, centrika-docs!101).
   * Доступным описанием диалога остаётся `message`: оно должно само по себе объяснять действие.
   */
  details?: ReactNode
  /** Кнопки в указанном порядке; не передан/пуст → дефолтные «Подтвердить»/«Отмена». */
  buttons?: ConfirmDialogButton[]
  /** Закрытие без выбора кнопки — крестик и Escape (и клик вне панели). */
  onClose?: () => void
  /**
   * Закрытие временно заблокировано (например, пока идёт выполнение действия): крестик получает
   * `disabled`/`aria-disabled`, теряет hover/pointer-афорданс и убирается из tab-order, а Escape и
   * клик вне панели становятся no-op — чтобы все три «выхода» были согласованы с недоступными
   * кнопками. По умолчанию выводится из отсутствия `onClose` (крестику нечего закрывать).
   */
  closeDisabled?: boolean
  /** Дефолтная кнопка «Подтвердить» (только когда `buttons` не передан/пуст). */
  onConfirm?: () => void
  /** Дефолтная кнопка «Отмена» (только когда `buttons` не передан/пуст). */
  onCancel?: () => void
}

const DEFAULT_TITLE = 'Подтвердите действие'
const DEFAULT_MESSAGE = 'Вы уверены, что хотите продолжить?'

export function ConfirmDialog({
  open,
  title,
  message,
  details,
  buttons,
  onClose,
  onConfirm,
  onCancel,
  closeDisabled,
}: ConfirmDialogProps) {
  // Дефолты — независимо для каждого непереданного/пустого параметра.
  const resolvedTitle = title && title.length > 0 ? title : DEFAULT_TITLE
  const resolvedMessage = message && message.length > 0 ? message : DEFAULT_MESSAGE
  // Крестику нечего закрывать без onClose — тогда он заблокирован по умолчанию.
  const resolvedCloseDisabled = closeDisabled ?? onClose == null
  // Непустой переданный список ПОЛНОСТЬЮ заменяет дефолт; иначе — две дефолтные кнопки.
  const resolvedButtons: ConfirmDialogButton[] =
    buttons && buttons.length > 0
      ? buttons
      : [
          { label: 'Подтвердить', variant: 'primary', onClick: onConfirm },
          { label: 'Отмена', variant: 'secondary', onClick: onCancel },
        ]

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Крестик, Escape и клик вне панели у Radix идут через onOpenChange(false) — трактуем как
        // закрытие без выбора кнопки. Открытие снаружи не порождает onOpenChange(true) здесь.
        // При заблокированном закрытии (идёт действие) все три «выхода» — no-op.
        if (!next && !resolvedCloseDisabled) onClose?.()
      }}
    >
      <DialogContent title={resolvedTitle} description={resolvedMessage} className="max-w-narrow">
        <Card
          header={
            <div className="flex items-center justify-between gap-3">
              <span>{resolvedTitle}</span>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Закрыть"
                  disabled={resolvedCloseDisabled}
                  aria-disabled={resolvedCloseDisabled || undefined}
                  className={cn(
                    '-mr-1 rounded-8 p-1 text-txt-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    resolvedCloseDisabled
                      ? 'pointer-events-none opacity-50'
                      : 'hover:bg-hover hover:text-txt',
                  )}
                >
                  <X className="size-4 stroke-current" aria-hidden />
                </button>
              </DialogClose>
            </div>
          }
        >
          {details ? <div className="mb-4">{details}</div> : null}
          <p className="text-txt-secondary">{resolvedMessage}</p>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            {resolvedButtons.map((btn, i) => (
              <Button
                key={i}
                type="button"
                variant={btn.variant}
                size="sm"
                disabled={btn.disabled}
                onClick={btn.onClick}
              >
                {btn.label}
              </Button>
            ))}
          </div>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
